import React, {
	useEffect,
	useMemo,
} from 'react';

import {
	Alert,
	AlertIcon,
	Badge,
	Box,
	Button,
	Flex,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	FormLabel,
	Heading,
	HStack,
	Icon,
	Input,
	Select,
	SimpleGrid,
	Spinner,
	Stack,
	Text,
	Textarea,
	VStack,
} from '@chakra-ui/react';

import {
	FiArrowLeft,
	FiMessageSquare,
	FiPlus,
} from 'react-icons/fi';

import {
	Link as RouterLink,
	useNavigate,
	useParams,
} from 'react-router-dom';

import {
	useForm,
	Controller,
} from 'react-hook-form';

import { toast } from 'react-toastify';

import Card from 'components/card/Card';

import { 	templateCategories,
	templatesLanguages } from './helpers';

import {
	useCreateMetaWhatsappTemplateMutation,
	useListMetaWhatsappTemplatesQuery,
	useListSettingsChannelsQuery,
} from 'api/metaWhatsappApi';

import {
	BODY_MAX,
	FOOTER_MAX,
	HEADER_MAX,
	TEMPLATE_NAME_MAX,
	buildTemplateComponents,
	validateButtons,
	BUTTON_TEXT_MAX,
	extractPositionalVariables,
	insertVariableAt,
	normalizeTemplateName,
	renderTemplatePreview,
	validatePositionalVariables,
	validateHeaderVariables,
} from './utils/metaWhatsappTemplate';
import TemplateButtons from './TemplateButtons';
import TemplateBubble from '../../_components/TemplateBubble';

const templateRoute = (channelId) =>
	`/whatsapp/settings/meta/templates/${encodeURIComponent(
		channelId
	)}`;

function VariableExamples({
	title,
	variables,
	register,
	fieldName,
}) {
	if (!variables.length) return null;

	return (
		<Box>
			<Text
				fontSize="sm"
				fontWeight="600"
				color="text.primary"
				mb="2"
			>
				{title}
			</Text>

			<SimpleGrid
				columns={{
					base: 1,
					md: 2,
				}}
				spacing="3"
			>
				{variables.map((index) => (
					<FormControl key={index}>
						<FormLabel
							fontSize="xs"
							color="text.muted"
						>
							{`{{${index}}}`} example
						</FormLabel>

						<Input
							size="sm"
							placeholder={`Example value for {{${index}}}`}
							{...register(
								`${fieldName}.${index}`,
								{
									required:
										'Example value is required',
								}
							)}
						/>
					</FormControl>
				))}
			</SimpleGrid>
		</Box>
	);
}

function CharacterCount({
	value,
	max,
}) {
	const count = String(value ?? '').length;

	return (
		<Text
			fontSize="xs"
			color={
				count > max
					? 'danger'
					: 'text.muted'
			}
		>
			{count}/{max}
		</Text>
	);
}

function TemplatePreview({
	category,
	language,
	header,
	body,
	footer,
	headerExamples,
	bodyExamples,
	buttons,
}) {
	const previewHeader =
		renderTemplatePreview(
			header,
			headerExamples
		);

	const previewBody =
		renderTemplatePreview(
			body,
			bodyExamples
		);

	return (
		<Box
			position={{
				base: 'static',
				xl: 'sticky',
			}}
			top="6"
		>
			<HStack
				justify="space-between"
				mb="3"
			>
				<Heading size="sm">
					Preview
				</Heading>

				<HStack spacing="1.5">
					<Badge variant="subtle">
						{category}
					</Badge>

					<Badge variant="subtle">
						{language}
					</Badge>
				</HStack>
			</HStack>

			<Box
				bg="bg.subtle"
				borderRadius="xl"
				borderWidth="1px"
				borderColor="border.default"
				p={{
					base: '4',
					md: '6',
				}}
				minH="360px"
			>
				{/* ★ THE SAME BUBBLE THE CAMPAIGN WIZARD DRAWS. Two drawings of "what
				    the phone shows" drift, and the one people trust least is whichever
				    they saw second. */}
				<TemplateBubble
					header={previewHeader}
					body={previewBody}
					footer={footer}
					buttons={(buttons ?? []).filter((b) => String(b?.text ?? '').trim())}
				/>
			</Box>
		</Box>
	);
}

export default function CreateMetaWhatsappTemplate() {
	const { channelId } = useParams();
	const navigate = useNavigate();

	const {
		data: channelsResponse,
		isLoading: channelsLoading,
		isError: channelsError,
	} = useListSettingsChannelsQuery();

	const {
		data: existingTemplates = [],
		isLoading: templatesLoading,
	} =
		useListMetaWhatsappTemplatesQuery(
			{},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const [
		createTemplate,
		{ isLoading: creating },
	] =
		useCreateMetaWhatsappTemplateMutation();

	const channels =
		channelsResponse?.channels ?? [];

	const channel = useMemo(
		() =>
			channels.find(
				(item) =>
					String(item.channelId) ===
					String(channelId)
			),
		[channels, channelId]
	);

	const defaultLanguage =
		templatesLanguages?.some(
			(item) => item.value === 'en_US'
		)
			? 'en_US'
			: templatesLanguages?.[0]?.value ??
			  'en';

	const {
		register,
		handleSubmit,
		watch,
		getValues,
		setValue,
		trigger,
		control,
		formState: {
			errors,
			isSubmitting,
		},
	} = useForm({
		mode: 'onBlur',

		defaultValues: {
			name: '',
			language: defaultLanguage,
			category: 'MARKETING',
			header: '',
			body: '',
			footer: '',
			buttons: [],
			headerExamples: {},
			bodyExamples: {},
		},
	});

	const values = watch();

	const headerVariables = useMemo(
		() =>
			extractPositionalVariables(
				values.header
			),
		[values.header]
	);

	const bodyVariables = useMemo(
		() =>
			extractPositionalVariables(
				values.body
			),
		[values.body]
	);

	const templatesForWaba = useMemo(() => {
		if (!channel?.wabaId) return [];

		return existingTemplates.filter(
			(template) =>
				String(template.wabaId) ===
				String(channel.wabaId)
		);
	}, [
		existingTemplates,
		channel?.wabaId,
	]);

	const nameRegistration = register(
		'name',
		{
			required: 'Template name is required',

			maxLength: {
				value: TEMPLATE_NAME_MAX,
				message: `Template name cannot exceed ${TEMPLATE_NAME_MAX} characters`,
			},

			pattern: {
				value: /^[a-z0-9_]+$/,
				message:
					'Use lowercase letters, numbers and underscores only.',
			},

			validate: (value) => {
				const normalized =
					normalizeTemplateName(value);

				const language =
					getValues('language');

				const duplicate =
					templatesForWaba.some(
						(template) =>
							template.name ===
								normalized &&
							template.language ===
								language
					);

				return (
					!duplicate ||
					'A template with this name and language already exists.'
				);
			},
		}
	);

	const languageRegistration =
		register('language', {
			required: 'Language is required',
		});

	// The textarea's DOM node, so "Add variable" can read the caret. React Hook
	// Form owns the ref it hands to the input, so both are kept: its callback
	// is still called, and the node is stored here as well.
	const bodyRef = React.useRef(null);

	const bodyRegistration =
		register('body', {
			required:
				'Message body is required',

			maxLength: {
				value: BODY_MAX,
				message: `Body cannot exceed ${BODY_MAX} characters`,
			},

			validate:
				validatePositionalVariables,
		});

	useEffect(() => {
		if (values.language) {
			trigger('name');
		}
	}, [
		values.language,
		trigger,
	]);

	/**
	 * Insert the next variable WHERE THE CARET IS.
	 *
	 * ★ IT USED TO APPEND TO THE END, AND THAT IS ALWAYS INVALID.
	 * Meta refuses a component whose text begins or ends with a parameter
	 * ("Leading or Trailing Params Not Allowed", error 100), which
	 * `validateVariablePlacement` enforces. Appending therefore produced the
	 * one shape the validator exists to reject, and with `shouldValidate` the
	 * error appeared on the very click meant to help — the button fought its
	 * own form. Inserting at the caret lets someone write "Hi ", click, and
	 * carry on typing, which is the normal way to build these.
	 *
	 * Validation is deliberately NOT forced here: the field is `mode: 'onBlur'`,
	 * so placement is judged when the sentence is finished rather than
	 * mid-thought. A genuinely trailing variable is still caught on blur and
	 * again on submit.
	 */
	const addBodyVariable = () => {
		const current =
			getValues('body') ?? '';

		const el = bodyRef.current;

		// Without a mounted textarea there is no caret to honour, and
		// `insertVariableAt` falls back to the end of the text.
		const at =
			el &&
			typeof el.selectionStart === 'number'
				? el.selectionStart
				: null;

		const { value, caret } =
			insertVariableAt(
				current,
				at
			);

		setValue('body', value, {
			shouldDirty: true,
			shouldValidate: false,
		});

		// Put the caret after the token so typing continues where it left off —
		// which is also what stops the variable from staying trailing.
		window.requestAnimationFrame(() => {
			if (!el) return;
			el.focus();
			el.setSelectionRange(
				caret,
				caret
			);
		});
	};

	const onSubmit = async (form) => {
		if (!channel?.channelId) {
			toast.error(
				'WhatsApp channel is unavailable.'
			);
			return;
		}

		const components =
			buildTemplateComponents({
				header: form.header,
				headerExamples:
					form.headerExamples,
				body: form.body,
				bodyExamples:
					form.bodyExamples,
				footer: form.footer,
				buttons: form.buttons,
			});

		const request = {
			channelId: channel.channelId,
			name: normalizeTemplateName(
				form.name
			),
			language: form.language,
			category: form.category,
			parameterFormat: 'POSITIONAL',
			components,
		};

		try {
			const result =
				await createTemplate(
					request
				).unwrap();

			toast.success(
				result?.template?.status ===
					'APPROVED'
					? 'Template created and approved.'
					: 'Template submitted to Meta for review.'
			);

			navigate(
				templateRoute(channelId),
				{
					replace: true,
				}
			);
		} catch (error) {
			const data = error?.data ?? {};
			// ★ NAME WHO REFUSED. Meta's own phrasing ("Leading or Trailing
			// Params Not Allowed") is accurate and unhelpful on its own; saying
			// that Meta rejected the definition tells the agent the request got
			// through and the content is what to change. A refusal from our own
			// side keeps its own reason so the two are never confused.
			const message =
				data.error === 'TEMPLATE_INVALID' && data.metaMessage
					? `Meta rejected this template: ${data.metaMessage}`
					: data.metaMessage ||
						data.message ||
						data.error ||
						'Template could not be created.';

			toast.error(message);
		}
	};

	if (
		channelsLoading ||
		templatesLoading
	) {
		return (
			<Flex
				minH="320px"
				align="center"
				justify="center"
			>
				<Spinner />
			</Flex>
		);
	}

	if (
		channelsError ||
		!channel
	) {
		return (
			<Alert
				status="error"
				borderRadius="lg"
			>
				<AlertIcon />
				The WhatsApp channel could not be loaded.
			</Alert>
		);
	}

	if (!channel.wabaId) {
		return (
			<Alert
				status="warning"
				borderRadius="lg"
			>
				<AlertIcon />
				This channel has no WABA configuration.
			</Alert>
		);
	}

	const displayNumber =
		channel.label ||
		channel.displayPhoneNumber ||
		channel.phoneNumberId;

	return (
		<VStack
			align="stretch"
			spacing="5"
			maxW="1400px"
			mx="auto"
			w="full"
		>
			<Box>
				<Button
					as={RouterLink}
					to={templateRoute(
						channelId
					)}
					variant="ghost"
					size="sm"
					leftIcon={
						<Icon
							as={FiArrowLeft}
						/>
					}
					mb="3"
				>
					Templates
				</Button>

				<HStack
					align="start"
					spacing="3"
				>
					<Flex
						w="10"
						h="10"
						bg="bg.subtle"
						borderRadius="lg"
						align="center"
						justify="center"
						flexShrink="0"
					>
						<Icon
							as={
								FiMessageSquare
							}
							color="text.accent"
							boxSize="5"
						/>
					</Flex>

					<Box>
						<Heading
							size="md"
							color="text.primary"
						>
							Create message template
						</Heading>

						<Text
							fontSize="sm"
							color="text.muted"
							mt="1"
						>
							{displayNumber}
						</Text>
					</Box>
				</HStack>
			</Box>

			<Alert
				status="info"
				borderRadius="lg"
				fontSize="sm"
			>
				<AlertIcon />

				New templates are submitted to Meta for
				review. Their status will update through
				template synchronization and webhooks.
			</Alert>

			<Flex
				direction={{
					base: 'column',
					xl: 'row',
				}}
				gap="6"
				align="start"
			>
				<Card
					flex="1"
					w="full"
					bg="bg.surface"
					borderWidth="1px"
					borderColor="border.default"
				>
					<form
						onSubmit={handleSubmit(
							onSubmit
						)}
					>
						<Stack spacing="6">
							<Box>
								<Heading
									size="sm"
									mb="1"
								>
									Template details
								</Heading>

								<Text
									fontSize="sm"
									color="text.muted"
								>
									Name, language and
									category cannot be
									treated as message
									content.
								</Text>
							</Box>

							<SimpleGrid
								columns={{
									base: 1,
									md: 2,
								}}
								spacing="4"
							>
								<FormControl
									isInvalid={
										errors.name
									}
								>
									<FormLabel>
										Template name
									</FormLabel>

									<Input
										placeholder="property_follow_up"
										{...nameRegistration}
										onChange={(
											event
										) => {
											event.target.value =
												normalizeTemplateName(
													event
														.target
														.value
												);

											nameRegistration.onChange(
												event
											);
										}}
									/>

									<FormErrorMessage>
										{
											errors
												.name
												?.message
										}
									</FormErrorMessage>

									<FormHelperText>
										Lowercase letters,
										numbers and
										underscores.
									</FormHelperText>
								</FormControl>

								<FormControl
									isInvalid={
										errors.language
									}
								>
									<FormLabel>
										Language
									</FormLabel>
<Select
	{...languageRegistration}
	onChange={(event) => {
		languageRegistration.onChange(event);
		trigger('name');
	}}
>
	{templatesLanguages.map((language) => (
		<option
			key={language.value}
			value={language.value}
		>
			{language.label}
		</option>
	))}
</Select>

									<FormErrorMessage>
										{
											errors
												.language
												?.message
										}
									</FormErrorMessage>
								</FormControl>
							</SimpleGrid>

							<FormControl>
								<FormLabel>
									Category
								</FormLabel>

							<Select
	{...register('category', {
		required: 'Category is required',
	})}
>
	{templateCategories.map((category) => (
		<option
			key={category.value}
			value={category.value}
		>
			{category.label}
		</option>
	))}
</Select>

								<FormHelperText>
									Authentication
									templates should use a
									dedicated authentication
									template flow.
								</FormHelperText>
							</FormControl>

							<Box
								h="1px"
								bg="border.default"
							/>

							<FormControl
								isInvalid={
									errors.header
								}
							>
								<Flex
									justify="space-between"
									align="center"
									mb="2"
								>
									<FormLabel
										mb="0"
									>
										Header
										(optional)
									</FormLabel>

									<CharacterCount
										value={
											values.header
										}
										max={
											HEADER_MAX
										}
									/>
								</Flex>

								<Input
									placeholder="Example: Exclusive offer"
									{...register(
										'header',
										{
											maxLength:
												{
													value: HEADER_MAX,
													message: `Header cannot exceed ${HEADER_MAX} characters`,
												},

											// One variable at most, and never first or last — both
											// are Meta rules, both used to surface only after submit.
											validate:
												validateHeaderVariables,
										}
									)}
								/>

								<FormErrorMessage>
									{
										errors
											.header
											?.message
									}
								</FormErrorMessage>
							</FormControl>

							<VariableExamples
								title="Header examples"
								variables={
									headerVariables
								}
								register={register}
								fieldName="headerExamples"
							/>

							<FormControl
								isInvalid={
									errors.body
								}
							>
								<Flex
									justify="space-between"
									align="center"
									mb="2"
								>
									<FormLabel
										mb="0"
									>
										Message body
									</FormLabel>

									<CharacterCount
										value={
											values.body
										}
										max={
											BODY_MAX
										}
									/>
								</Flex>

								<Textarea
									rows={7}
									resize="vertical"
									placeholder="Hi {{1}}, are you still interested in {{2}}?"
									{...bodyRegistration}
									ref={(el) => {
										bodyRegistration.ref(el);
										bodyRef.current = el;
									}}
								/>

								<Flex
									justify="space-between"
									align="center"
									mt="2"
									gap="3"
								>
									<FormErrorMessage
										mt="0"
									>
										{
											errors
												.body
												?.message
										}
									</FormErrorMessage>

									<Button
										type="button"
										size="xs"
										variant="outline"
										leftIcon={
											<Icon
												as={
													FiPlus
												}
											/>
										}
										onClick={
											addBodyVariable
										}
										ml="auto"
									>
										Add variable
									</Button>
								</Flex>
							</FormControl>

							<VariableExamples
								title="Body variable examples"
								variables={
									bodyVariables
								}
								register={register}
								fieldName="bodyExamples"
							/>

							<FormControl
								isInvalid={
									errors.footer
								}
							>
								<Flex
									justify="space-between"
									align="center"
									mb="2"
								>
									<FormLabel
										mb="0"
									>
										Footer
										(optional)
									</FormLabel>

									<CharacterCount
										value={
											values.footer
										}
										max={
											FOOTER_MAX
										}
									/>
								</Flex>

								<Input
									placeholder="Example: Reply STOP to opt out"
									{...register(
										'footer',
										{
											maxLength:
												{
													value: FOOTER_MAX,
													message: `Footer cannot exceed ${FOOTER_MAX} characters`,
												},

											validate: (
												value
											) =>
												!String(
													value ??
														''
												).includes(
													'{{'
												) ||
												'Footer variables are not supported.',
										}
									)}
								/>

								<FormErrorMessage>
									{
										errors
											.footer
											?.message
									}
								</FormErrorMessage>
							</FormControl>

							{/* ★ VALIDATED AS A GROUP, NOT FIELD BY FIELD. Nearly every
							    rule Meta enforces here is about the SET — two buttons
							    with the same label, three links where two are allowed,
							    a second call button — so a per-input validator could
							    not see the problem at all. */}
							<FormControl
								isInvalid={
									errors.buttons
								}
							>
								<Controller
									name="buttons"
									control={control}
									rules={{
										validate:
											validateButtons,
									}}
									render={({
										field,
									}) => (
										<TemplateButtons
											value={
												field.value
											}
											onChange={
												field.onChange
											}
										/>
									)}
								/>

								<FormErrorMessage>
									{
										errors
											.buttons
											?.message
									}
								</FormErrorMessage>
							</FormControl>

							<Flex
								justify="flex-end"
								gap="3"
								direction={{
									base: 'column-reverse',
									sm: 'row',
								}}
							>
								<Button
									as={RouterLink}
									to={templateRoute(
										channelId
									)}
									variant="ghost"
									isDisabled={
										creating
									}
								>
									Cancel
								</Button>

								<Button
									type="submit"
									colorScheme="brand"
									isLoading={
										creating ||
										isSubmitting
									}
									loadingText="Submitting"
								>
									Submit to Meta
								</Button>
							</Flex>
						</Stack>
					</form>
				</Card>

				<Box
					w={{
						base: 'full',
						xl: '390px',
					}}
					flexShrink="0"
				>
					<TemplatePreview
						category={
							values.category
						}
						language={
							values.language
						}
						header={values.header}
						body={values.body}
						footer={values.footer}
						buttons={values.buttons}
						headerExamples={
							values.headerExamples
						}
						bodyExamples={
							values.bodyExamples
						}
					/>
				</Box>
			</Flex>
		</VStack>
	);
}